import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Calendar, Clock, MapPin, Truck, Store, CheckCircle, AlertTriangle, BookmarkCheck } from 'lucide-react';
import { formatDeliveryDate, DELIVERY_TIME_SLOT } from '@/utils/deliveryDateUtils';
import { Gift } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HAMPER_IMAGES = {
  'smallPVC-hamper':       '/images/products/smallPVC-hamper.jpg',
  'kraftbox-hamper':       '/images/products/kraftbox-hamper.jpg',
  'base8-hamper':          '/images/products/base8-hamper.jpg',
  'base10-hamper':         '/images/products/base10-hamper.jpg',
  'pvc-medium-hamper':     '/images/products/pvc-medium-hamper.jpg',
  'corrugated-box-hamper': '/images/products/corrugated-box-hamper.jpg',
};
const DEFAULT_HAMPER_IMG = 'https://images.pexels.com/photos/28769885/pexels-photo-28769885.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const Checkout = () => {
  const { cart, clearCart, fetchCart } = useCart();
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState('');
  const [hamperDeliveryDate, setHamperDeliveryDate] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: user?.phone || '',
    delivery_address: '',
    delivery_type: 'delivery'
  });
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [calculatingDelivery, setCalculatingDelivery] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [savedAddressLoaded, setSavedAddressLoaded] = useState(false);
  const addressSuggRef = useRef(null);
  const suggestDebounceRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;        // wait for auth state to resolve
    if (!user) {
      toast.error('Please login to checkout');
      navigate('/login');
      return;
    }
    fetchCart();
    
    // Get delivery dates from cart page
    const savedDate = sessionStorage.getItem('selectedDeliveryDate');
    if (savedDate) {
      setSelectedDeliveryDate(savedDate);
    } else {
      const now = new Date();
      if (now.getHours() >= 11) {
        now.setDate(now.getDate() + 1);
        if (now.getDay() === 0) now.setDate(now.getDate() + 1);
      }
      setSelectedDeliveryDate(now.toISOString().split('T')[0]);
    }
    const hamperDate = sessionStorage.getItem('hamperDeliveryDate');
    if (hamperDate) setHamperDeliveryDate(hamperDate);
  }, [user, authLoading, navigate]);

  // Derived: cart item types
  const hasHamperItems = cart?.items?.some(i => i.item_type === 'gift_hamper');
  const hasRegularItems = cart?.items?.some(i => i.item_type !== 'gift_hamper');
  const hamperItems = cart?.items?.filter(i => i.item_type === 'gift_hamper') || [];
  const hasOneHamperItem = hamperItems.length == 1 && hamperItems.some(i => i.quantity == 1); 
  // Show "delivery as per actuals" when there are multiple hampers OR any hamper has qty > 1
  const showHamperDeliveryActuals = hamperItems.length > 0 || hamperItems.some(i => i.quantity > 0);

  // If regular item delivery date is same as hamper delivery date
  const hasSameDeliverydate = sessionStorage.getItem('selectedDeliveryDate') == sessionStorage.getItem('hamperDeliveryDate');

  useEffect(() => {
    if (user?.phone) {
      setFormData(prev => ({ ...prev, phone: user.phone }));
    }
  }, [user]);

  // Load saved default address on mount
  useEffect(() => {
    if (!user || !token) return;
    axios.get(`${API}/user/default-address`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const addr = res.data?.address;
        if (addr) {
          setFormData(prev => ({ ...prev, delivery_address: addr }));
          setSavedAddressLoaded(true);
          // Auto-calculate delivery for the saved address if delivery type is set
          if (formData.delivery_type === 'delivery') {
            setTimeout(() => calculateDelivery(addr), 300);
          }
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (addressSuggRef.current && !addressSuggRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const calculateDelivery = async (address) => {
    if (!address || formData.delivery_type === 'pickup') {
      setDeliveryInfo(null);
      return;
    }

    setCalculatingDelivery(true);
    setAddressError('');
    try {
      const response = await axios.post(`${API}/delivery/calculate`, { delivery_address: address });
      const data = response.data;
      if (data.address_found === false) {
        setDeliveryInfo(null);
        setAddressError(data.error || 'Address not found. Please provide complete address with area/sector and city.');
        // Auto-fetch suggestions to help the user
        fetchSuggestions(address);
      } else {
        setDeliveryInfo(data);
        setAddressError('');
      }
    } catch (error) {
      setDeliveryInfo(null);
      setAddressError('Could not verify address. Please try a more specific address.');
    } finally {
      setCalculatingDelivery(false);
    }
  };

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 3 || !token) return;
    try {
      const res = await axios.get(`${API}/address/suggestions?q=${encodeURIComponent(q)}`,
        { headers: { Authorization: `Bearer ${token}` } });
      const sugg = res.data?.suggestions || [];
      setAddressSuggestions(sugg);
      setShowSuggestions(sugg.length > 0);
    } catch {}
  }, [token]);

  const handleAddressChange = (address) => {
    setFormData({ ...formData, delivery_address: address });
    setAddressError('');
    setDeliveryInfo(null);        // clear stale delivery info immediately
    setSavedAddressLoaded(false);
    if (address.length >= 3) {
      clearTimeout(suggestDebounceRef.current);
      suggestDebounceRef.current = setTimeout(() => fetchSuggestions(address), 350);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (description) => {
    setFormData(prev => ({ ...prev, delivery_address: description }));
    setAddressSuggestions([]);
    setShowSuggestions(false);
    setAddressError('');
    setSavedAddressLoaded(false);
    // Trigger delivery calc with the selected suggestion
    calculateDelivery(description);
  };

  const handleDeliveryTypeChange = (type) => {
    setFormData({ ...formData, delivery_type: type });
    if (type === 'delivery' && formData.delivery_address) {
      calculateDelivery(formData.delivery_address);
    } else {
      setDeliveryInfo(null);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleRazorpayPayment = async (orderData) => {
    const scriptLoaded = await loadRazorpayScript();
    
    if (!scriptLoaded) {
      toast.error('Failed to load payment gateway. Please try again.');
      setLoading(false);
      return;
    }

    const options = {
      key: orderData.razorpay_key_id,
      amount: orderData.amount,
      currency: 'INR',
      name: 'HHouse of Breadss',
      description: `Order #${orderData.order_id.slice(0, 8).toUpperCase()}`,
      order_id: orderData.razorpay_order_id,
      handler: async function (response) {
        try {
          // Verify payment on backend
          await axios.post(
            `${API}/orders/verify-payment`,
            {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id: orderData.order_id
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          toast.success('Payment successful! Order confirmed.');
          await clearCart();
          
          // Open WhatsApp with pre-composed message
          if (orderData.whatsapp_url) {
            window.open(orderData.whatsapp_url, '_blank');
          }
          
          navigate(`/orders/${orderData.order_id}`);
        } catch (error) {
          console.error('Payment verification failed:', error);
          toast.error('Payment verification failed. Please contact support.');
          navigate(`/orders/${orderData.order_id}`);
        }
      },
      prefill: {
        name: formData.name,
        contact: formData.phone
      },
      theme: {
        color: '#D97706'
      },
      modal: {
        ondismiss: function () {
          setLoading(false);
          toast.info('Payment cancelled. Your order is saved as pending.');
          navigate(`/orders/${orderData.order_id}`);
        }
      }
    };

    const razorpay = new window.Razorpay(options);

    // Handle explicit payment failure events
    razorpay.on('payment.failed', async function (response) {
      try {
        await axios.post(
          `${API}/orders/payment-failed`,
          {
            order_id: orderData.order_id,
            error_code: response.error?.code || '',
            error_description: response.error?.description || response.error?.reason || ''
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error('Failed to report payment failure:', err);
      }
      setLoading(false);
      toast.error('Payment failed. Your order is saved — please try again or contact us.');
      navigate(`/orders/${orderData.order_id}`);
    });

    razorpay.open();
  };

  const validateAndNormalizePhone = (phone) => {
    let cleaned = phone.replace(/\s/g, '').replace(/-/g, '');
    if (cleaned.startsWith('+91')) cleaned = cleaned.slice(3);
    else if (cleaned.startsWith('91') && cleaned.length === 12) cleaned = cleaned.slice(2);
    if (!/^\d{10}$/.test(cleaned)) return null;
    return cleaned;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields - address only required for delivery
    if (!formData.name || !formData.phone) {
      toast.error('Please fill name and phone number');
      return;
    }

    const normalizedPhone = validateAndNormalizePhone(formData.phone);
    if (!normalizedPhone) {
      toast.error('Please enter a valid 10-digit phone number (e.g. 9876543210 or +91-9876543210)');
      return;
    }

    if (formData.delivery_type === 'delivery' && !formData.delivery_address) {
      toast.error('Please enter your delivery address');
      return;
    }

    if (formData.delivery_type === 'delivery' && addressError) {
      toast.error('Please select a valid delivery address from the suggestions below.');
      return;
    }

    if (formData.delivery_type === 'delivery' && !deliveryInfo) {
      toast.error('Please verify your delivery address — select it from the dropdown suggestions.');
      return;
    }

    if (cart?.items?.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const confirmAndPlaceOrder = async () => {
    setShowConfirmDialog(false);
    setLoading(true);

    try {
      // For pickup, set address to store location
      const orderData = {
        ...formData,
        phone: validateAndNormalizePhone(formData.phone) || formData.phone,
        delivery_address: formData.delivery_type === 'pickup' 
          ? 'Store Pickup - M3M Cosmopolitan, Sec 66, Gurugram' 
          : formData.delivery_address,
        scheduled_date: selectedDeliveryDate,
        hamper_delivery_date: hamperDeliveryDate || undefined
      };

      const orderResponse = await axios.post(
        `${API}/orders/create`,
        orderData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Clear the stored delivery date
      sessionStorage.removeItem('selectedDeliveryDate');
      sessionStorage.removeItem('hamperDeliveryDate');

      // If Razorpay order was created, open payment modal
      if (orderResponse.data.razorpay_order_id) {
        await handleRazorpayPayment(orderResponse.data);
      } else {
        // Fallback to pending payment flow
        toast.success('Order placed successfully! Payment pending.');
        await clearCart();
        
        if (orderResponse.data.whatsapp_url) {
          window.open(orderResponse.data.whatsapp_url, '_blank');
        }
        
        navigate(`/orders/${orderResponse.data.order_id}`);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to create order');
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <div className="flex-1 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-accent mb-8" data-testid="checkout-title">
            Checkout
          </h1>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card className="p-6">
                <h2 className="text-xl font-semibold text-accent mb-6">Delivery Details</h2>
                
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="checkout-form">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1"
                      required
                      data-testid="checkout-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="mt-1"
                      required
                      data-testid="checkout-phone-input"
                    />
                  </div>

                  <div>
                    <Label>Delivery Type *</Label>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-2">
                      <button
                        type="button"
                        onClick={() => handleDeliveryTypeChange('delivery')}
                        className={`flex-1 p-3 sm:p-4 rounded-xl border-2 transition-all ${
                          formData.delivery_type === 'delivery'
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 hover:border-primary/50'
                        }`}
                        data-testid="delivery-option"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-xl sm:text-2xl">🚚</span>
                          <div className="text-left">
                            <p className="font-semibold text-sm sm:text-base">Home Delivery</p>
                            <p className="text-xs text-muted-foreground">₹11/km (₹35 flat within 3.5km)</p>
                          </div>
                        </div>
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => handleDeliveryTypeChange('pickup')}
                        className={`flex-1 p-3 sm:p-4 rounded-xl border-2 transition-all ${
                          formData.delivery_type === 'pickup'
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 hover:border-primary/50'
                        }`}
                        data-testid="pickup-option"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-xl sm:text-2xl">🏪</span>
                          <div className="text-left">
                            <p className="font-semibold text-sm sm:text-base">Store Pickup</p>
                            <p className="text-xs text-muted-foreground">Free - No charges</p>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address">
                      {formData.delivery_type === 'delivery' ? 'Delivery Address *' : 'Pickup From'}
                    </Label>
                    {formData.delivery_type === 'delivery' ? (
                      <div ref={addressSuggRef} className="relative mt-1">
                        {/* Saved address badge */}
                        {savedAddressLoaded && (
                          <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-md px-2 py-1 mb-2">
                            <BookmarkCheck className="h-3.5 w-3.5" />
                            Saved address loaded — you can update it below
                          </div>
                        )}
                        <Textarea
                          id="address"
                          value={formData.delivery_address}
                          onChange={(e) => handleAddressChange(e.target.value)}
                          onFocus={() => { if (formData.delivery_address.length >= 3) fetchSuggestions(formData.delivery_address); }}
                          onBlur={() => {
                            // Delay hide so clicks on suggestions register
                            setTimeout(() => setShowSuggestions(false), 200);
                            if (formData.delivery_address.length > 10 && !deliveryInfo) {
                              calculateDelivery(formData.delivery_address);
                            }
                          }}
                          rows={3}
                          required
                          data-testid="checkout-address-input"
                          className={addressError ? 'border-red-400 focus:border-red-500' : ''}
                          placeholder="Start typing your address — select from suggestions for accurate delivery charges"
                        />

                        {/* Autocomplete dropdown */}
                        {showSuggestions && addressSuggestions.length > 0 && (
                          <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-lg shadow-xl max-h-52 overflow-y-auto">
                            {addressSuggestions.map((s, i) => (
                              <div
                                key={i}
                                className="flex items-start gap-2 px-3 py-2.5 text-sm hover:bg-amber-50 cursor-pointer border-b last:border-0"
                                onMouseDown={() => selectSuggestion(s.description)}
                              >
                                <MapPin className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                                <span>{s.description}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Address error with suggestions prompt */}
                        {addressError && (
                          <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-red-700">{addressError}</p>
                                <p className="text-xs text-red-600 mt-1">
                                  Please type more details (sector, area, landmark) and select from the suggestions dropdown.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {calculatingDelivery && (
                          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Calculating delivery charge...
                          </p>
                        )}
                        {deliveryInfo && !calculatingDelivery && !addressError && (
                          <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center gap-1.5 text-green-700 text-sm font-semibold">
                              <CheckCircle className="h-4 w-4" /> Address verified
                            </div>
                            {!hasSameDeliverydate && ( 
                            <p className="text-sm text-muted-foreground mt-1">
                              Distance: {deliveryInfo.distance_km} km &nbsp;·&nbsp;
                              {deliveryInfo.is_free_delivery ? (
                                <span className="text-green-600 font-semibold">Free Delivery!</span>
                              ) : (
                                <span>Delivery charge: <strong>₹{deliveryInfo.delivery_charge}</strong></span>
                              )}
                            </p>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-1 p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="font-semibold text-accent">HHouse of Breadss</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          R3/015A, M3M Cosmopolitan<br />
                          Sector 66, Gurugram, Haryana
                        </p>
                        <p className="text-sm text-success font-semibold mt-2">
                          ✓ No delivery charges
                        </p>
                      </div>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-medium transition-all hover:scale-105 active:scale-95 shadow-lg"
                    disabled={loading}
                    data-testid="place-order-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Place Order'
                    )}
                  </Button>
                </form>
              </Card>
            </div>

            <div className="lg:col-span-1">
              <Card className="p-6 sticky top-24" data-testid="order-summary">
                <h2 className="text-xl font-semibold text-accent mb-4">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  {cart?.items?.map((item) => {
                    const isHamper = item.item_type === 'gift_hamper';
                    const name = isHamper ? item.hamper_name : item.product?.name;
                    const price = isHamper ? item.unit_price : item.product?.price;
                    const key = isHamper ? item.hamper_cart_id : item.product_id;

                    if (isHamper) {
                      return (
                        <div key={key} className="rounded-xl border border-amber-200 bg-amber-50/40 overflow-hidden" data-testid={`summary-item-${key}`}>
                          <div className="flex gap-3 p-3">
                            {/* Hamper image */}
                            <div className="h-16 w-16 rounded-lg overflow-hidden flex-shrink-0 border border-amber-100">
                              <img
                                src={HAMPER_IMAGES[item.hamper_id] || DEFAULT_HAMPER_IMG}
                                alt={name}
                                className="w-full h-full object-cover"
                                onError={e => { e.target.src = DEFAULT_HAMPER_IMG; }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start gap-2">
                                <span className="text-sm font-semibold text-accent flex items-center gap-1">
                                  <Gift className="h-3 w-3 text-amber-500 flex-shrink-0" />
                                  {name}
                                </span>
                                <span className="text-sm font-bold text-primary flex-shrink-0">₹{((price || 0) * item.quantity).toFixed(2)}</span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">Qty: {item.quantity}</p>
                            </div>
                          </div>
                          {/* Customisation notice */}
                          <div className="px-3 pb-3">
                            <p className="text-sm text-black-700 rounded-lg px-3 py-2 flex items-start gap-1.5">
                              <Gift className="h-3 w-3 flex-shrink-0 mt-0.5 text-black-600" />
                              For further customisation, our team will reach out to you.{showHamperDeliveryActuals && ' Delivery will be as per actuals.'}
                            </p>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={key} className="flex justify-between text-sm" data-testid={`summary-item-${key}`}>
                        <span className="text-muted-foreground">{name} x {item.quantity}</span>
                        <span className="font-semibold">₹{((price || 0) * item.quantity).toFixed(2)}</span>
                      </div>
                    );
                  })}
                  
                  <div className="border-t border-amber-100 pt-3">
                    <div className="flex justify-between text-muted-foreground mb-2">
                      <span>Subtotal</span>
                      <span data-testid="summary-subtotal">₹{cart?.total?.toFixed(2)}</span>
                    </div>
                    {!hasSameDeliverydate && (
                      <div className="flex justify-between text-muted-foreground mb-2">
                        <span>Delivery</span>
                        {formData.delivery_type === 'pickup' ? (
                          <span className="text-success font-semibold">PICKUP</span>
                        ) : deliveryInfo ? (
                          <span className={deliveryInfo.is_free_delivery ? 'text-success font-semibold' : ''}>
                            {deliveryInfo.is_free_delivery ? 'FREE' : `₹${deliveryInfo.delivery_charge}`}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">TBD</span>
                        )}
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold text-accent">
                      <span>Total</span>
                      <span data-testid="summary-total">
                        ₹{(
                          (cart?.total || 0) +
                          (!hasSameDeliverydate && formData.delivery_type === 'delivery' && deliveryInfo ? deliveryInfo.delivery_charge : 0)
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {!hasSameDeliverydate && hasHamperItems && (
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 text-sm text-muted-foreground">
                  <p className="mb-1">NOTE: YOUR DELIVERY WILL BE ON ACTUALS</p>
                </div>
                )}
                {hasSameDeliverydate && hasHamperItems && (
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 text-sm text-muted-foreground">
                  <p className="mb-1">NOTE: YOUR GIFT HAMPER DELIVERY WILL BE ON ACTUALS</p>
                </div>
                )}

                <div className="mt-4 bg-amber-50 rounded-lg p-4 text-sm text-muted-foreground">
                  <p className="mb-2">✓ All products are 100% eggless</p>
                  <p>✓ Fresh daily baking</p>
                </div>

                {/* Delivery Date(s) Display */}
                {hasRegularItems && selectedDeliveryDate && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 text-blue-700 text-xs font-semibold mb-1">
                      <Truck className="h-3.5 w-3.5" /> Regular Items Delivery
                    </div>
                    <p className="text-accent font-semibold text-sm">{formatDeliveryDate(new Date(selectedDeliveryDate))}</p>
                    <p className="text-xs text-muted-foreground">{DELIVERY_TIME_SLOT}</p>
                  </div>
                )}
                {hasHamperItems && hamperDeliveryDate && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg border-2 border-amber-300">
                    <div className="flex items-center gap-2 text-amber-700 text-xs font-semibold mb-1">
                      <Gift className="h-3.5 w-3.5" /> Gift Hamper Delivery
                    </div>
                    <p className="text-accent font-semibold text-sm">{formatDeliveryDate(new Date(hamperDeliveryDate))}</p>
                    <p className="text-xs text-amber-600">3 days from order placement</p>
                  </div>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <CheckCircle className="h-6 w-6 text-primary" />
              Confirm Your Order
            </DialogTitle>
            <DialogDescription>
              Please confirm your delivery/pickup details before placing the order.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Delivery/Pickup Type */}
            <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
              {formData.delivery_type === 'pickup' ? (
                <Store className="h-8 w-8 text-primary" />
              ) : (
                <Truck className="h-8 w-8 text-primary" />
              )}
              <div>
                <p className="font-semibold text-accent">
                  {formData.delivery_type === 'pickup' ? 'Store Pickup' : 'Home Delivery'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {formData.delivery_type === 'pickup' 
                    ? 'M3M Cosmopolitan, Sec 66, Gurugram' 
                    : formData.delivery_address}
                </p>
              </div>
            </div>

            {/* Date & Time */}
            {hasRegularItems && selectedDeliveryDate && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Truck className="h-7 w-7 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">Regular Items</p>
                  <p className="font-semibold text-accent">{formatDeliveryDate(new Date(selectedDeliveryDate))}</p>
                  <p className="text-xs text-blue-600">{DELIVERY_TIME_SLOT}</p>
                </div>
              </div>
            )}
            {hasHamperItems && hamperDeliveryDate && (
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border-2 border-amber-300">
                <Gift className="h-7 w-7 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Gift Hampers</p>
                  <p className="font-semibold text-accent">{formatDeliveryDate(new Date(hamperDeliveryDate))}</p>
                  <p className="text-xs text-amber-600">3 days from order placement</p>
                </div>
              </div>
            )}

            {/* Order Total */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-muted-foreground">Order Total</span>
              <span className="text-xl font-bold text-primary">
                ₹{((cart?.total || 0) + ((hasOneHamperItem || hasRegularItems) ? (deliveryInfo?.delivery_charge || 0) : 0)).toFixed(2)}
              </span>
            </div>

            {/* Customer Details */}
            <div className="text-sm text-muted-foreground">
              <p><strong>Name:</strong> {formData.name}</p>
              <p><strong>Phone:</strong> {formData.phone}</p>
            </div>
          </div>

          <DialogFooter className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              className="flex-1"
            >
              Go Back
            </Button>
            <Button
              onClick={confirmAndPlaceOrder}
              className="flex-1 bg-primary hover:bg-primary/90"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm & Pay'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default Checkout;
